import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "wouter";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle, Loader2, ChevronRight, Sparkles, Search } from "lucide-react";
import SupportChat from "@/components/SupportChat";

type AdminConversation = {
  otherUserId: string;
  otherUserName: string | null;
  lastMessage: string;
  lastMessageAt: string | null;
  unreadCount: number;
};

type LookupUser = { id: string; email: string | null; name: string };

export default function AdminMessages() {
  const queryClient = useQueryClient();
  const search = useSearch();
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [manualTarget, setManualTarget] = useState<{ id: string; name: string | null } | null>(null);
  const [composerQuery, setComposerQuery] = useState("");
  const autoExpandedRef = useRef(false);

  const { data: conversations = [], isLoading } = useQuery<AdminConversation[]>({
    queryKey: ["/api/admin/support-conversations"],
    queryFn: async () => {
      const res = await fetch("/api/admin/support-conversations", { credentials: "include" });
      if (!res.ok) throw new Error(`support-conversations ${res.status}`);
      return res.json();
    },
    refetchInterval: 15_000,
    // Global staleTime is Infinity (trap #1): without this, an empty result
    // cached before any messages existed would stick forever and the inbox
    // would show "no conversations" even when there are some. Always refetch.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const expand = (userId: string | null) => {
    setExpandedUserId((prev) => {
      const next = prev === userId ? null : userId;
      if (next) {
        queryClient.setQueryData<AdminConversation[]>(
          ["/api/admin/support-conversations"],
          (current = []) =>
            current.map((c) => (c.otherUserId === next ? { ...c, unreadCount: 0 } : c)),
        );
      }
      return next;
    });
  };

  // ?user=<id> opens a thread directly (from a feedback row that had a userId).
  const targetUserId = useMemo(() => new URLSearchParams(search).get("user"), [search]);
  const targetName = useMemo(() => new URLSearchParams(search).get("name"), [search]);
  // ?email=<email> comes from a feedback row filed logged-out (no userId) — we
  // resolve it to an account so the operator can still reach them in-app.
  const targetEmail = useMemo(() => new URLSearchParams(search).get("email"), [search]);

  const { data: emailMatches } = useQuery<LookupUser[]>({
    queryKey: ["/api/admin/users/lookup", "email", targetEmail],
    enabled: !!targetEmail,
    queryFn: async () => {
      const res = await fetch(`/api/admin/users/lookup?q=${encodeURIComponent(targetEmail!)}`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json();
    },
  });

  useEffect(() => {
    if (autoExpandedRef.current) return;
    if (targetUserId) {
      autoExpandedRef.current = true;
      setManualTarget({ id: targetUserId, name: targetName });
      setExpandedUserId(targetUserId);
      return;
    }
    if (targetEmail && emailMatches) {
      autoExpandedRef.current = true;
      const lower = targetEmail.toLowerCase();
      const hit =
        emailMatches.find((u) => (u.email || "").toLowerCase() === lower) || emailMatches[0];
      if (hit) {
        setManualTarget({ id: hit.id, name: hit.name });
        setExpandedUserId(hit.id);
      }
    }
  }, [targetUserId, targetName, targetEmail, emailMatches]);

  const { data: searchResults = [], isFetching: isSearching } = useQuery<LookupUser[]>({
    queryKey: ["/api/admin/users/lookup", "compose", composerQuery.trim()],
    enabled: composerQuery.trim().length >= 2,
    queryFn: async () => {
      const res = await fetch(`/api/admin/users/lookup?q=${encodeURIComponent(composerQuery.trim())}`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const openWith = (user: LookupUser) => {
    setManualTarget({ id: user.id, name: user.name });
    setExpandedUserId(user.id);
    setComposerQuery("");
  };

  const rows: AdminConversation[] = useMemo(() => {
    if (manualTarget && !conversations.some((c) => c.otherUserId === manualTarget.id)) {
      return [
        {
          otherUserId: manualTarget.id,
          otherUserName: manualTarget.name,
          lastMessage: "",
          lastMessageAt: null,
          unreadCount: 0,
        },
        ...conversations,
      ];
    }
    return conversations;
  }, [conversations, manualTarget]);

  const emailUnresolved = !!targetEmail && emailMatches != null && emailMatches.length === 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="container mx-auto max-w-2xl px-4 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold tracking-tight">Support messages</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Direct messages between you and users.
          </p>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={composerQuery}
            onChange={(e) => setComposerQuery(e.target.value)}
            placeholder="Message a user — search by name or email"
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary"
          />
          {composerQuery.trim().length >= 2 && (
            <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-border bg-background shadow-lg">
              {isSearching && (
                <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                </div>
              )}
              {!isSearching && searchResults.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No matching users.</div>
              )}
              {searchResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => openWith(u)}
                  className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-muted"
                >
                  <span className="text-sm font-medium">{u.name}</span>
                  {u.email && <span className="text-xs text-muted-foreground">{u.email}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {emailUnresolved && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            No Stoop account matches <span className="font-medium">{targetEmail}</span> — this
            feedback was filed without signing in, so there's no in-app inbox to reach. Reply by
            email instead.
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && rows.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <MessageCircle className="h-12 w-12 text-muted-foreground/40" />
            <div>
              <h3 className="text-lg font-semibold">No support conversations yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Search for a user above to start one, or use "Message this user" on a feedback
                report.
              </p>
            </div>
          </div>
        )}

        {!isLoading && rows.length > 0 && (
          <div className="space-y-2">
            {rows.map((conv) => {
              const expanded = expandedUserId === conv.otherUserId;
              const name = conv.otherUserName || "User";
              return (
                <Card
                  key={conv.otherUserId}
                  className={conv.unreadCount > 0 ? "border-primary/30 bg-primary/5" : ""}
                >
                  <CardContent className="p-0">
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 p-4 text-left"
                      onClick={() => expand(conv.otherUserId)}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
                        <Sparkles className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-semibold text-sm">{name}</span>
                          {conv.unreadCount > 0 && (
                            <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">
                          {conv.lastMessage || "No messages yet"}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {(() => {
                            if (!conv.lastMessageAt) return "";
                            try {
                              return new Date(conv.lastMessageAt).toLocaleString(undefined, {
                                dateStyle: "short",
                                timeStyle: "short",
                              });
                            } catch {
                              return "";
                            }
                          })()}
                        </p>
                      </div>
                      <ChevronRight
                        className={`h-4 w-4 shrink-0 text-muted-foreground transition ${expanded ? "rotate-90" : ""}`}
                      />
                    </button>

                    {expanded && (
                      <div className="border-t px-4 pb-4">
                        <SupportChat otherUserId={conv.otherUserId} otherPartyName={name} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
